/**
 * Local TOFU pins for conversation E2EE wraps.
 *
 * Distinct from `adieuu-device-signature-verification` (opt-in fingerprint
 * confirmation). First successful fetch pins automatically; later device or
 * member-set changes require an explicit accept in Member Security.
 */

const DB_NAME = 'adieuu-peer-key-pins';
const DB_VERSION = 1;
const IDENTITY_STORE = 'identities';
const CONVERSATION_STORE = 'conversations';

export interface PinnedPeerDevice {
  deviceId: string;
  ecdhPublicKey: string;
  kemPublicKey: string;
}

export interface PinnedPeerIdentity {
  identityId: string;
  signingPublicKey: string;
  devices: PinnedPeerDevice[];
  pinnedAt: string;
}

export interface PinnedConversationMembers {
  conversationId: string;
  identityIds: string[];
  pinnedAt: string;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDENTITY_STORE)) {
        db.createObjectStore(IDENTITY_STORE, { keyPath: 'identityId' });
      }
      if (!db.objectStoreNames.contains(CONVERSATION_STORE)) {
        db.createObjectStore(CONVERSATION_STORE, { keyPath: 'conversationId' });
      }
    };
  });
}

function idbRequest<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result as T);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
  });
}

export async function getPeerIdentityPin(identityId: string): Promise<PinnedPeerIdentity | null> {
  try {
    const db = await openDb();
    const tx = db.transaction(IDENTITY_STORE, 'readonly');
    const raw = await idbRequest(
      tx.objectStore(IDENTITY_STORE).get(identityId) as IDBRequest<PinnedPeerIdentity | undefined>,
    );
    db.close();
    return raw ?? null;
  } catch {
    return null;
  }
}

export async function setPeerIdentityPin(record: PinnedPeerIdentity): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(IDENTITY_STORE, 'readwrite');
  await idbRequest(tx.objectStore(IDENTITY_STORE).put(record));
  db.close();
}

export async function getConversationMemberPin(
  conversationId: string,
): Promise<PinnedConversationMembers | null> {
  try {
    const db = await openDb();
    const tx = db.transaction(CONVERSATION_STORE, 'readonly');
    const raw = await idbRequest(
      tx.objectStore(CONVERSATION_STORE).get(conversationId) as IDBRequest<
        PinnedConversationMembers | undefined
      >,
    );
    db.close();
    return raw ?? null;
  } catch {
    return null;
  }
}

export async function setConversationMemberPin(record: PinnedConversationMembers): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(CONVERSATION_STORE, 'readwrite');
  await idbRequest(tx.objectStore(CONVERSATION_STORE).put(record));
  db.close();
}

export async function addConversationMemberPin(
  conversationId: string,
  identityId: string,
): Promise<void> {
  const existing = await getConversationMemberPin(conversationId);
  const identityIds = new Set(existing?.identityIds ?? []);
  identityIds.add(identityId);
  await setConversationMemberPin({
    conversationId,
    identityIds: [...identityIds].sort(),
    pinnedAt: existing?.pinnedAt ?? new Date().toISOString(),
  });
}

/** Test helper: wipe the pin database. */
export function deletePeerKeyPinDatabase(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve();
      return;
    }
    const req = indexedDB.deleteDatabase(DB_NAME);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
    req.onblocked = () => resolve();
  });
}
